'use client';

import { useState, useEffect } from 'react';
import { Label } from './label';
import { Input } from './input';

interface ColorPickerProps {
  label?: string;
  primaryColor?: string;
  accentColor?: string;
  onPrimaryColorChange?: (color: string) => void;
  onAccentColorChange?: (color: string) => void;
  className?: string;
}

export function ColorPicker({
  label = 'Theme Colors',
  primaryColor = '#3b82f6',
  accentColor = '#f59e0b',
  onPrimaryColorChange,
  onAccentColorChange,
  className = '',
}: ColorPickerProps) {
  const [primaryHex, setPrimaryHex] = useState(primaryColor);
  const [accentHex, setAccentHex] = useState(accentColor);

  // Update hex inputs when props change
  useEffect(() => {
    setPrimaryHex(primaryColor);
  }, [primaryColor]);

  useEffect(() => {
    setAccentHex(accentColor);
  }, [accentColor]);

  const handlePrimaryColorChange = (color: string) => {
    setPrimaryHex(color);
    onPrimaryColorChange?.(color);
  };

  const handleAccentColorChange = (color: string) => {
    setAccentHex(color);
    onAccentColorChange?.(color);
  };

  const handlePrimaryHexChange = (hex: string) => {
    setPrimaryHex(hex);
    // Only update if it's a valid hex color
    if (/^#[0-9A-F]{6}$/i.test(hex)) {
      onPrimaryColorChange?.(hex);
    }
  };

  const handleAccentHexChange = (hex: string) => {
    setAccentHex(hex);
    // Only update if it's a valid hex color
    if (/^#[0-9A-F]{6}$/i.test(hex)) {
      onAccentColorChange?.(hex);
    }
  };

  const handlePrimaryHexBlur = () => {
    // Ensure the hex is valid on blur, if not revert to the original
    if (!/^#[0-9A-F]{6}$/i.test(primaryHex)) {
      setPrimaryHex(primaryColor);
    }
  };

  const handleAccentHexBlur = () => {
    // Ensure the hex is valid on blur, if not revert to the original
    if (!/^#[0-9A-F]{6}$/i.test(accentHex)) {
      setAccentHex(accentColor);
    }
  };

  return (
    <div className={`space-y-3 ${className}`}>
      {label && <Label>{label}</Label>}
      <div className="space-y-4">
        {/* Primary Color */}
        <div className="space-y-2">
          <Label htmlFor="primaryColor" className="text-sm font-medium">
            Primary Color
          </Label>
          <div className="flex items-center gap-3">
            <input
              id="primaryColor"
              type="color"
              value={primaryColor}
              onChange={(e) => handlePrimaryColorChange(e.target.value)}
              className="h-10 w-16 cursor-pointer rounded border border-gray-300 bg-white"
            />
            <div className="flex-1">
              <Input
                type="text"
                value={primaryHex}
                onChange={(e) => handlePrimaryHexChange(e.target.value)}
                onBlur={handlePrimaryHexBlur}
                placeholder="#3b82f6"
                className="font-mono text-sm"
              />
            </div>
          </div>
        </div>

        {/* Accent Color */}
        <div className="space-y-2">
          <Label htmlFor="accentColor" className="text-sm font-medium">
            Accent Color
          </Label>
          <div className="flex items-center gap-3">
            <input
              id="accentColor"
              type="color"
              value={accentColor}
              onChange={(e) => handleAccentColorChange(e.target.value)}
              className="h-10 w-16 cursor-pointer rounded border border-gray-300 bg-white"
            />
            <div className="flex-1">
              <Input
                type="text"
                value={accentHex}
                onChange={(e) => handleAccentHexChange(e.target.value)}
                onBlur={handleAccentHexBlur}
                placeholder="#f59e0b"
                className="font-mono text-sm"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
